import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Sms as SmsIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { GatewaySMS, GatewaySMSFormData } from '../../types';
import { gatewaySmsService } from '../../services/gatewaySmsService';

type GatewaySMSType = 'twilio' | 'zenvia' | 'torpedussms' | 'infobip';

interface GatewaySMSFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (gatewayData: GatewaySMSFormData) => Promise<void>;
  gateway?: GatewaySMS | null;
  loading?: boolean;
}

interface FormErrors {
  nome?: string;
  tipo?: string;
  account_sid?: string;
  auth_token?: string;
  phone_number?: string;
  api_token_zenvia?: string;
  from_zenvia?: string;
  usuario_torpedussms?: string;
  senha_torpedussms?: string;
  api_key_infobip?: string;
  base_url_infobip?: string;
  sender_name_infobip?: string;
  webhook_url?: string;
}

const initialFormData: GatewaySMSFormData = {
  nome: '',
  tipo: 'twilio',
  ativo: true,
  is_default: false,
  observacoes: '',
};

const GatewaySMSForm: React.FC<GatewaySMSFormProps> = ({
  open,
  onClose,
  onSubmit,
  gateway,
  loading = false,
}) => {
  const [formData, setFormData] = useState<GatewaySMSFormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  const isEditing = !!gateway;

  // Carregar dados do gateway para edição
  useEffect(() => {
    if (isEditing && gateway) {
      setFormData({
        nome: gateway.nome,
        tipo: gateway.tipo,
        ativo: gateway.ativo,
        is_default: gateway.is_default,
        
        // Campos Twilio
        account_sid: gateway.account_sid || '',
        auth_token: gateway.auth_token || '',
        phone_number: gateway.phone_number || '',
        
        // Campos Zenvia
        api_token_zenvia: gateway.api_token_zenvia || '',
        from_zenvia: gateway.from_zenvia || '',
        
        // Campos TorpedusSMS
        usuario_torpedussms: gateway.usuario_torpedussms || '',
        senha_torpedussms: gateway.senha_torpedussms || '',
        
        // Campos Infobip
        api_key_infobip: gateway.api_key_infobip || '',
        base_url_infobip: gateway.base_url_infobip || '',
        sender_name_infobip: gateway.sender_name_infobip || '',
        
        // Campos comuns
        webhook_url: gateway.webhook_url || '',
        observacoes: gateway.observacoes || '',
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    setSubmitError('');
  }, [gateway, isEditing, open]);

  const handleInputChange = (field: keyof GatewaySMSFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field as keyof FormErrors]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof GatewaySMSFormData) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar campos específicos ao mudar tipo
    if (field === 'tipo') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        // Limpar campos específicos dos outros tipos
        account_sid: '',
        auth_token: '',
        phone_number: '',
        api_token_zenvia: '',
        from_zenvia: '',
        usuario_torpedussms: '',
        senha_torpedussms: '',
        api_key_infobip: '',
        base_url_infobip: '',
        sender_name_infobip: '',
      }));
    }
    
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field as keyof FormErrors]: undefined }));
    }
  };

  const handleSwitchChange = (field: keyof GatewaySMSFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validações básicas
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.tipo) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    // Validações específicas por tipo
    switch (formData.tipo) {
      case 'twilio':
        if (!formData.account_sid?.trim()) {
          newErrors.account_sid = 'Account SID é obrigatório';
        }
        if (!formData.auth_token?.trim()) {
          newErrors.auth_token = 'Auth Token é obrigatório';
        }
        if (!formData.phone_number?.trim()) {
          newErrors.phone_number = 'Número de telefone é obrigatório';
        }
        break;
      
      case 'zenvia':
        if (!formData.api_token_zenvia?.trim()) {
          newErrors.api_token_zenvia = 'API Token é obrigatório';
        }
        if (!formData.from_zenvia?.trim()) {
          newErrors.from_zenvia = 'From é obrigatório';
        }
        break;
      
      case 'torpedussms':
        if (!formData.usuario_torpedussms?.trim()) {
          newErrors.usuario_torpedussms = 'Usuário é obrigatório';
        }
        if (!formData.senha_torpedussms?.trim()) {
          newErrors.senha_torpedussms = 'Senha é obrigatória';
        }
        break;
      
      case 'infobip':
        if (!formData.api_key_infobip?.trim()) {
          newErrors.api_key_infobip = 'API Key é obrigatória';
        }
        if (!formData.base_url_infobip?.trim()) {
          newErrors.base_url_infobip = 'Base URL é obrigatória';
        }
        if (!formData.sender_name_infobip?.trim()) {
          newErrors.sender_name_infobip = 'Sender Name é obrigatório';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error: any) {
      console.error('Erro no formulário:', error);
      setSubmitError(error.message || 'Erro ao salvar gateway SMS');
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    setSubmitError('');
    setShowPasswords({});
    onClose();
  };

  // Renderizar campos de configuração específicos por tipo
  const renderConfigurationFields = () => {
    switch (formData.tipo) {
      case 'twilio':
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
              <SmsIcon sx={{ mr: 1 }} />
              Configurações Twilio
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account SID"
                  value={formData.account_sid || ''}
                  onChange={handleInputChange('account_sid')}
                  error={!!errors.account_sid}
                  helperText={errors.account_sid}
                  required
                  type={showPasswords.account_sid ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('account_sid')}
                          edge="end"
                        >
                          {showPasswords.account_sid ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Auth Token"
                  value={formData.auth_token || ''}
                  onChange={handleInputChange('auth_token')}
                  error={!!errors.auth_token}
                  helperText={errors.auth_token}
                  required
                  type={showPasswords.auth_token ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('auth_token')}
                          edge="end"
                        >
                          {showPasswords.auth_token ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Número de Telefone"
                  value={formData.phone_number || ''}
                  onChange={handleInputChange('phone_number')}
                  error={!!errors.phone_number}
                  helperText={errors.phone_number || 'Ex: +5511999999999'}
                  required
                  placeholder="+5511999999999"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 'zenvia':
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
              <SmsIcon sx={{ mr: 1 }} />
              Configurações Zenvia
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Token"
                  value={formData.api_token_zenvia || ''}
                  onChange={handleInputChange('api_token_zenvia')}
                  error={!!errors.api_token_zenvia}
                  helperText={errors.api_token_zenvia}
                  required
                  type={showPasswords.api_token_zenvia ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('api_token_zenvia')}
                          edge="end"
                        >
                          {showPasswords.api_token_zenvia ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="From"
                  value={formData.from_zenvia || ''}
                  onChange={handleInputChange('from_zenvia')}
                  error={!!errors.from_zenvia}
                  helperText={errors.from_zenvia || 'Identificador do remetente'}
                  required
                  placeholder="minha-empresa"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 'torpedussms':
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
              <SmsIcon sx={{ mr: 1 }} />
              Configurações TorpedusSMS
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Usuário"
                  value={formData.usuario_torpedussms || ''}
                  onChange={handleInputChange('usuario_torpedussms')}
                  error={!!errors.usuario_torpedussms}
                  helperText={errors.usuario_torpedussms || 'Seu usuário no TorpedusSMS'}
                  required
                  placeholder="seuusuario"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Senha"
                  value={formData.senha_torpedussms || ''}
                  onChange={handleInputChange('senha_torpedussms')}
                  error={!!errors.senha_torpedussms}
                  helperText={errors.senha_torpedussms || 'Sua senha no TorpedusSMS'}
                  required
                  type={showPasswords.senha_torpedussms ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('senha_torpedussms')}
                          edge="end"
                        >
                          {showPasswords.senha_torpedussms ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 'infobip':
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
              <SmsIcon sx={{ mr: 1 }} />
              Configurações Infobip
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Key"
                  value={formData.api_key_infobip || ''}
                  onChange={handleInputChange('api_key_infobip')}
                  error={!!errors.api_key_infobip}
                  helperText={errors.api_key_infobip || 'Sua API Key do Infobip'}
                  required
                  type={showPasswords.api_key_infobip ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('api_key_infobip')}
                          edge="end"
                        >
                          {showPasswords.api_key_infobip ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Base URL"
                  value={formData.base_url_infobip || ''}
                  onChange={handleInputChange('base_url_infobip')}
                  error={!!errors.base_url_infobip}
                  helperText={errors.base_url_infobip || 'Ex: https://api.infobip.com'}
                  required
                  placeholder="https://api.infobip.com"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sender Name"
                  value={formData.sender_name_infobip || ''}
                  onChange={handleInputChange('sender_name_infobip')}
                  error={!!errors.sender_name_infobip}
                  helperText={errors.sender_name_infobip || 'Nome do remetente (máx. 11 caracteres)'}
                  required
                  placeholder="MinhaEmpresa"
                  inputProps={{ maxLength: 11 }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SmsIcon />
          {isEditing ? 'Editar Gateway SMS' : 'Novo Gateway SMS'}
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Nome *"
                value={formData.nome}
                onChange={handleInputChange('nome')}
                error={!!errors.nome}
                helperText={errors.nome || 'Nome identificador do gateway'}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={!!errors.tipo}>
                <InputLabel>Tipo *</InputLabel>
                <Select
                  value={formData.tipo}
                  onChange={handleSelectChange('tipo')}
                  label="Tipo *"
                >
                  <MenuItem value="twilio">
                    <Box display="flex" alignItems="center" gap={1}>
                      📱 Twilio
                    </Box>
                  </MenuItem>
                  <MenuItem value="zenvia">
                    <Box display="flex" alignItems="center" gap={1}>
                      📨 Zenvia
                    </Box>
                  </MenuItem>
                  <MenuItem value="torpedussms">
                    <Box display="flex" alignItems="center" gap={1}>
                      🚀 TorpedusSMS
                    </Box>
                  </MenuItem>
                  <MenuItem value="infobip">
                    <Box display="flex" alignItems="center" gap={1}>
                      🌐 Infobip
                    </Box>
                  </MenuItem>
                </Select>
                {errors.tipo && (
                  <Typography variant="caption" color="error">
                    {errors.tipo}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.ativo}
                    onChange={handleSwitchChange('ativo')}
                    color="primary"
                  />
                }
                label="Ativo"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_default}
                    onChange={handleSwitchChange('is_default')}
                    color="primary"
                    icon={<StarBorderIcon />}
                    checkedIcon={<StarIcon />}
                  />
                }
                label="Padrão"
              />
            </Grid>
          </Grid>

          {renderConfigurationFields()}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Webhook URL"
                value={formData.webhook_url || ''}
                onChange={handleInputChange('webhook_url')}
                error={!!errors.webhook_url}
                helperText={errors.webhook_url || 'URL para receber notificações (opcional)'}
                placeholder="https://meusite.com/webhook/sms"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                value={formData.observacoes || ''}
                onChange={handleInputChange('observacoes')}
                multiline
                rows={3}
                placeholder="Observações sobre este gateway SMS..."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<SmsIcon />}
          >
            {loading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GatewaySMSForm;